// ---------------------------------------------------------------------------
// Benchmark chart types — aligned with backend Pydantic models
// ---------------------------------------------------------------------------

/** A single point on the probability density function (KDE curve).
 *  Matches backend PDFDataPoint model. */
export interface BenchmarkPDFPoint {
  Density: number;
  Value: number;
}

/** A single bin of the benchmark histogram.
 *  Matches backend HistogramBin model. */
export interface HistogramBin {
  BinStart: number;
  BinEnd: number;
  Count: number;
}

/** Full benchmark data for a single building/matrikel.
 *  Matches backend BenchmarkDataPoint model. */
export interface BenchmarkDataPoint {
  MatrikelKey: string;
  Address: string;
  BFENummer: number;
  AverageBuildingYear: number | null;
  AverageKWhConsumptionSum: number;
  kWhPrYPrM2: number;
  /** Percentile position in the benchmark group CDF (0 = best, 1 = worst). */
  BenchmarkCDF?: number | null;
  /** Number of buildings in the benchmark group. */
  GroupCount?: number | null;
  GroupMean: number | null;
  GroupMedian: number | null;
  MeanDiff: number | null;
  MedianDiff: number | null;
  SumBuildingArea: number;
  TotalBuildings: number;
  TotalMeteringPoints: number;
  ExtractedBuildingUse: string;
  ExtractedHeatInstallation: string;
  BuildingUsesWithArea: string;
  OriginalBuildingUsesWithArea: string;
  HeatInstallationsWithArea: string;
  OutlierFlag: boolean;
  OutlierReason: string | null;
  EMOIntegrationSuccess: boolean | null;
  EnergyLabelClassification: string | null;
  EnergyLabelCountOverview: string | null;
  OverwriteFlag: boolean;
  VerificationFlag: boolean;
}

/** Response from the /detailed_benchmark/:accountId/:matrikelKey endpoint.
 *  Matches backend DetailedBenchmarkDataOutput model. */
export interface BenchmarkChartData {
  BenchmarkData: BenchmarkDataPoint;
  BenchmarkPDF: BenchmarkPDFPoint[];
  BenchmarkHistogram?: HistogramBin[];
  /** Raw anonymized kWhPrYPrM2 values — used to compute histogram bins dynamically. */
  BenchmarkHistogramValues?: number[];
}

/** Derived benchmark point used for the scatter marker on the chart */
export interface BenchmarkScatterPoint {
  x: number;
  y: number;
}

/** Props for the main PortfolioAnalysisBenchmarkChart component */
export interface PortfolioAnalysisBenchmarkChartProps {
  /** MatrikelKey – fallback identifier when BenchmarkData is unavailable */
  address?: string;
  /** Benchmark chart data from the API */
  data?: Partial<BenchmarkChartData>;
  /** Whether the data is currently loading */
  isLoading?: boolean;
}

/** Props for the BenchmarkKpiCards sub-component */
export interface BenchmarkKpiCardsProps {
  benchmarkData: BenchmarkDataPoint;
}

/** Props for the BenchmarkBuildingInfo sub-component */
export interface BenchmarkBuildingInfoProps {
  benchmarkData: BenchmarkDataPoint;
}

/** Props for the BenchmarkAssessment sub-component */
export interface BenchmarkAssessmentProps {
  benchmarkData: BenchmarkDataPoint;
  benchmarkPDF: BenchmarkPDFPoint[];
  isLoading?: boolean;
}

/**
 * Compute the cumulative distribution function (CDF) at a given x value
 * by integrating the PDF using the trapezoidal rule.
 */
export const computeCDF = (pdf: BenchmarkPDFPoint[], x: number): number => {
  if (pdf.length < 2) return 0;
  let area = 0;
  for (let i = 1; i < pdf.length; i++) {
    if (pdf[i].Value > x) {
      const fraction =
        (x - pdf[i - 1].Value) / (pdf[i].Value - pdf[i - 1].Value);
      const interpDensity =
        pdf[i - 1].Density + fraction * (pdf[i].Density - pdf[i - 1].Density);
      area +=
        ((x - pdf[i - 1].Value) * (pdf[i - 1].Density + interpDensity)) / 2;
      break;
    }
    const width = pdf[i].Value - pdf[i - 1].Value;
    area += (width * (pdf[i].Density + pdf[i - 1].Density)) / 2;
  }
  return Math.min(1, Math.max(0, area));
};
